import { useAuthStore } from '~~/stores/auth'
import custOFetch from '~/composables/useCustomFetch'
import type { User } from '~/types/prisma'

export const useAuth = () => {
  const config = useRuntimeConfig()
  const authStore = useAuthStore()
  const router = useRouter()
  const { captureException } = useSentry()

  const setUser = (user: User | null) => {
    authStore.setUser(user)
    return user
  }

  const login = async (
    email: string,
    password: string,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/auth/signin`, {
      method: 'POST',
      credentials: 'include',
      mode: 'cors',
      body: {
        email,
        password,
      },
    }, false)
    return data
  }

  const verify = async (
    verificationCode: string,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/auth/email/verify/${verificationCode}`, {
      method: 'GET',
      credentials: 'include',
    }, false)
    return authStore.user
  }

  const signup = async (
    email: string,
    firstName: string,
    lastName: string,
    password: string,
  ) => {
    const data: any = await $fetch(`${config.public.apiBase}/auth/signup`, {
      method: 'POST',
      credentials: 'include',
      mode: 'cors',
      body: {
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        password,
      },
    })
    return authStore.user
  }

  const invite_signup = async (
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    phone: string,
    token: string,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/auth/invite-signup`, {
      method: 'POST',
      credentials: 'include',
      body: {
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        password,
        phone,
        token,
      },
    }, false)
    return authStore.user
  }

  const event_invite_signup = async (
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    phone: string,
    token: string,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/auth/event-invite-signup`, {
      method: 'POST',
      credentials: 'include',
      body: {
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        password,
        phone,
        token,
      },
    }, false)
    return authStore.user
  }

  const leader_signup = async (
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    phone: string,
    token: string,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/auth/leader-signup`, {
      method: 'POST',
      credentials: 'include',
      body: {
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        password,
        phone,
        token,
      },
    }, false)
    return authStore.user
  }

  const judge_signup = async (
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    phone: string,
    token: string,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/auth/judge-signup`, {
      method: 'POST',
      credentials: 'include',
      body: {
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        password,
        phone,
        token,
      },
    }, false)
    return authStore.user
  }

  const angler_signup = async (
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    phone: string,
    token: string,
  ) => {
    const data: any = await custOFetch(`${config.public.apiBase}/auth/angler-signup`, {
      method: 'POST',
      credentials: 'include',
      body: {
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        password,
        phone,
        token,
      },
    }, false)
    return authStore.user
  }

  const resetPassword = async (
    email: string,
    newPassword: string,
    newPasswordToken: string,
  ) => {
    const data: any = await $fetch(`${config.public.apiBase}/auth/email/reset-password`, {
      method: 'POST',
      credentials: 'include',
      body: {
        email,
        newPassword,
        newPasswordToken,
      },
    })
    return data
  }

  const changePassword = async (
    payload: {
      currentPassword: string,
      newPassword: string,
    }
  ) => {
    const data: any = await $fetch(`${config.public.apiBase}/auth/email/change-password`, {
      method: 'POST',
      credentials: 'include',
      body: payload,
    })
    return data
  }

  const requestReset = async (
    email: string,
  ) => {
    const data: any = await $fetch(`${config.public.apiBase}/auth/email/forgot-password/${email}`, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
    })
    return data
  }

  const logout = async (redirect: string | null = "") => {
    try {
      await custOFetch(`${config.public.apiBase}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      }, false)
    } catch (error) {
      console.warn('Failed to call logout endpoint:', error)
    }
    
    setUser(null)
    
    if (redirect === null) return

    if (redirect) {
      return await router.push(redirect)
    }

    return await router.push('/login')
  }

  const exists = async (email: string) => {
    try {
      const data: null | 'exists' = await custOFetch(`${config.public.apiBase}/users/exists/${email}`, {
        method: 'GET',
        mode: 'cors',
      }, false)
      return data === 'exists'
    }
    catch (error) {
      captureException(error)
      window.console.log(error)
      throw error
    }
  }

  const me = async () => {
    try {
      const { eventId, getEventId } = useContext()
      let resolvedEventId = eventId.value
      const data: any = await custOFetch(`${config.public.apiBase}/users/me`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          ...(resolvedEventId && { 'x-event-id': resolvedEventId }),
        },
      }, false)
      await authStore.setUser(data)

      // If eventId was unavailable on first call (user not yet in store),
      // re-fetch with the correct event context now that we have user.id
      if (!resolvedEventId && data?.id) {
        resolvedEventId = getEventId()
        if (resolvedEventId) {
          const refreshed: any = await custOFetch(`${config.public.apiBase}/users/me`, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: { 'x-event-id': resolvedEventId },
          }, false)
          await authStore.setUser(refreshed)
          return refreshed
        }
      }

      return data
    }
    catch (error) {
      captureException(error)
      window.console.log(error)
    }

    return authStore.user
  }

  return {
    login,
    logout,
    signup,
    exists,
    me,
    verify,
    resetPassword,
    changePassword,
    requestReset,
    invite_signup,
    event_invite_signup,
    leader_signup,
    judge_signup,
    angler_signup,
  }
}
